import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Bell, CheckCircle2, Circle, Inbox, Landmark, type LucideIcon } from "lucide-react";
import { Card, CardContent, EmptyState } from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import { brokeragePortalApi, type BrokerageProduction } from "../api/brokerage";
import { portalPackagesApi } from "../api/packages";
import { brokerageStripeApi } from "../api/brokerageStripe";
import { usePortalNotifications } from "../components/PortalNotificationsContext";
import { LoadingTiles, PipelineMixBars, StatTile, SubBrokerTable } from "../components/BrokerageBits";

// Brokerage / Sub-Broker PR-6 — the Home tab: the firm's production at a glance
// (the portal twin of the staff Production view, re-scoped across the firm's
// active roster). Split-ledger totals are empty until a junior sub-broker exists;
// production (deals / loans) shows for independent-affiliated sub-brokers today.
//
// Brokerage Home "what needs attention" (this change) — the subtitle promises
// "…and what needs attention," so the tab now leads with a "Needs your attention"
// card (outstanding requests / unread notifications / payment setup, each a live
// signal linking to its tab) and, for a brand-new empty firm, replaces the bare
// zeros/empty-states with a self-clearing getting-started checklist. Both fetch
// their signals best-effort — a failed read just hides a row, never blocks the
// page — and the checklist disappears on its own once the firm has any activity.
export function BrokerageHomeTab() {
  // The bell/nav unread count is already shared via PortalNotificationsProvider
  // (mounted by PortalChrome for every portal); the Home attention card just
  // reads it. 0 outside the provider (tests) → the notifications row stays off.
  const { unreadCount } = usePortalNotifications();
  const [data, setData] = useState<BrokerageProduction | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Attention/getting-started signals — null while loading or on a failed read.
  const [requestsCount, setRequestsCount] = useState<number | null>(null);
  const [paymentConnected, setPaymentConnected] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const d = await brokeragePortalApi.production();
        if (!cancelled) setData(d);
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Couldn't load your firm's production.");
      }
    })();
    // Best-effort: a failed read hides its row (never blocks the page). Requests
    // uses the aggregate count endpoint; payment-setup reuses the existing Stripe
    // status read the Payment setup tab already uses.
    //
    // Reads `total` DELIBERATELY, preserving today's number. The endpoint now
    // also carries `needsYou`, but this card phrases its row as "N document
    // requests to complete" — switching the figure without rewriting that copy
    // would make the row claim the firm must act on items Phoenix is still
    // reviewing. Brokerage copy + figure move together in a follow-up; the
    // buyer and seller Homes are the ones that got the split in this PR.
    void portalPackagesApi.requestsCount().then(
      (c) => {
        if (!cancelled) setRequestsCount(c.total);
      },
      () => {},
    );
    void brokerageStripeApi.getStatus().then(
      (s) => {
        if (!cancelled) setPaymentConnected(s.onboardingComplete);
      },
      () => {},
    );
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) return <p className="text-small text-status-error">{error}</p>;
  if (!data) return <LoadingTiles />;

  // A "brand-new" firm has nothing to show — no sub-brokers and no deals in any
  // state. The getting-started checklist stands in for the bare zeros/empty-states
  // and self-clears the moment the firm has any activity.
  const isEmptyFirm =
    data.subBrokerCount === 0 && data.activeDealCount === 0 && data.closedDealCount === 0;

  return (
    <div className="space-y-space-5">
      <NeedsAttentionCard
        requestsCount={requestsCount}
        unreadCount={unreadCount}
        paymentConnected={paymentConnected}
      />

      {isEmptyFirm ? (
        <GettingStartedCard requestsCount={requestsCount} paymentConnected={paymentConnected} />
      ) : (
        <>
          <div className="grid gap-space-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatTile label="Sub-brokers">{data.subBrokerCount}</StatTile>
            <StatTile label="Deals sourced">
              {data.dealsSourcedCount}
              <span className="ml-space-2 text-small font-normal text-gray-mid">
                {formatCurrency(data.dealsSourcedVolume)}
              </span>
            </StatTile>
            <StatTile label="Loans originated">
              {data.loansOriginatedCount}
              <span className="ml-space-2 text-small font-normal text-gray-mid">
                {formatCurrency(data.loansOriginatedTotal)}
              </span>
            </StatTile>
            <StatTile label="Active / closed deals">
              {data.activeDealCount} / {data.closedDealCount}
            </StatTile>
          </div>

          <Card>
            <CardContent className="space-y-space-3">
              <h3 className="text-small font-semibold uppercase tracking-wide text-gray-mid">
                Pipeline
              </h3>
              <PipelineMixBars mix={data.pipelineMix} />
            </CardContent>
          </Card>

          <div className="max-w-xs">
            <StatTile label="Gross (Phoenix → firm)">{formatCurrency(data.totalGross)}</StatTile>
          </div>

          <div className="space-y-space-3">
            <h3 className="text-small font-semibold uppercase tracking-wide text-gray-mid">
              Sub-broker production ({data.subBrokers.length})
            </h3>
            {data.subBrokers.length === 0 ? (
              <EmptyState
                title="No sub-brokers yet"
                description="Your affiliated sub-brokers and their production will appear here."
              />
            ) : (
              <SubBrokerTable rows={data.subBrokers} />
            )}
          </div>
        </>
      )}
    </div>
  );
}

interface AttentionRow {
  key: string;
  icon: LucideIcon;
  label: string;
  to: string;
}

// "Needs your attention" — one row per live signal, each linking to the tab that
// resolves it. Hides rows whose signal is zero/unknown and hides the whole card
// when nothing is pending.
function NeedsAttentionCard({
  requestsCount,
  unreadCount,
  paymentConnected,
}: {
  requestsCount: number | null;
  unreadCount: number;
  paymentConnected: boolean | null;
}) {
  const rows: AttentionRow[] = [];
  if ((requestsCount ?? 0) > 0) {
    const n = requestsCount as number;
    rows.push({
      key: "requests",
      icon: Inbox,
      label: `${n} document request${n === 1 ? "" : "s"} to complete`,
      to: "?tab=requests",
    });
  }
  if (unreadCount > 0) {
    rows.push({
      key: "notifications",
      icon: Bell,
      label: `${unreadCount} unread notification${unreadCount === 1 ? "" : "s"}`,
      to: "?tab=notifications",
    });
  }
  // Only nag once we KNOW the account isn't connected (false); null = unknown.
  if (paymentConnected === false) {
    rows.push({
      key: "payment-setup",
      icon: Landmark,
      label: "Connect your bank to receive payouts",
      to: "?tab=payment-setup",
    });
  }

  if (rows.length === 0) return null;

  return (
    <Card data-testid="brokerage-needs-attention">
      <CardContent className="space-y-space-3">
        <h3 className="text-small font-semibold uppercase tracking-wide text-gray-mid m-0">
          Needs your attention
        </h3>
        <ul className="flex flex-col gap-space-2 list-none p-0 m-0">
          {rows.map((r) => {
            const Icon = r.icon;
            return (
              <li key={r.key}>
                <Link
                  to={r.to}
                  data-testid={`attention-${r.key}`}
                  className="group flex items-center gap-space-3 rounded-md border border-border-light bg-white-pure p-space-3 text-small text-onyx no-underline hover:border-gold hover:text-gold-shadow"
                >
                  <Icon size={18} strokeWidth={1.5} className="shrink-0 text-gold" aria-hidden />
                  <span className="flex-1">{r.label}</span>
                  <ArrowRight
                    size={16}
                    strokeWidth={1.5}
                    className="shrink-0 text-gray-mid group-hover:text-gold-shadow"
                    aria-hidden
                  />
                </Link>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}

interface GettingStartedStep {
  key: string;
  label: string;
  to: string;
  done: boolean;
}

// Self-clearing onboarding checklist shown only while the firm is genuinely empty
// (no sub-brokers, no deals). Mirrors the CheckCircle2/Circle row pattern used by
// the broker OnboardingProgressCard; completed steps strike through, incomplete
// steps are the CTA into the tab that completes them.
function GettingStartedCard({
  requestsCount,
  paymentConnected,
}: {
  requestsCount: number | null;
  paymentConnected: boolean | null;
}) {
  const steps: GettingStartedStep[] = [
    {
      key: "bank",
      label: "Connect your bank to receive payouts",
      to: "?tab=payment-setup",
      done: paymentConnected === true,
    },
    {
      key: "sub-broker",
      label: "Invite your first sub-broker",
      to: "?tab=roster",
      // Always the primary CTA here: inviting one gives the firm activity, which
      // clears the whole card.
      done: false,
    },
    {
      key: "requests",
      label: "Complete any outstanding requests",
      to: "?tab=requests",
      done: requestsCount === 0,
    },
  ];

  return (
    <Card data-testid="brokerage-getting-started">
      <CardContent className="space-y-space-4">
        <div className="space-y-space-1">
          <h3 className="text-h4 font-semibold text-onyx m-0">Welcome to your brokerage portal</h3>
          <p className="text-small text-gray-mid m-0">
            A few quick steps to get your firm set up. Your production, sub-brokers, and payouts will
            appear here as you go.
          </p>
        </div>
        <ul className="flex flex-col gap-space-2 list-none p-0 m-0">
          {steps.map((s) =>
            s.done ? (
              <li
                key={s.key}
                data-testid={`getting-started-${s.key}`}
                className="flex items-center gap-space-2 text-small"
              >
                <CheckCircle2
                  size={18}
                  strokeWidth={1.5}
                  className="shrink-0 text-status-success"
                  aria-hidden
                />
                <span className="text-gray-mid line-through">{s.label}</span>
              </li>
            ) : (
              <li key={s.key}>
                <Link
                  to={s.to}
                  data-testid={`getting-started-${s.key}`}
                  className="group flex items-center gap-space-2 text-small text-onyx no-underline hover:text-gold-shadow"
                >
                  <Circle size={18} strokeWidth={1.5} className="shrink-0 text-gray-mid" aria-hidden />
                  <span className="flex-1">{s.label}</span>
                  <ArrowRight
                    size={16}
                    strokeWidth={1.5}
                    className="shrink-0 text-gray-mid group-hover:text-gold-shadow"
                    aria-hidden
                  />
                </Link>
              </li>
            ),
          )}
        </ul>
      </CardContent>
    </Card>
  );
}
